/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { tb } from 'components/layouts/Table';
import CustomDialog from 'components/modals/common/CustomDialog';
import { findMpItemStatHis, saveMpItemStatHis } from 'apis/mp/MpItem';
import { MpItem } from 'models/mp/MpItem';
import CustomGrid from '../../grids/CustomGrid';
import { GlobalBtnGroup } from '../../layouts/ContentLayout';
import { Code } from '../../../models/common/CommonCode';
import { getCommonCodeNames } from '../../../apis/admin/CommonCode';
import { isEmpty } from 'lodash';
import MpItemStatProcess from '../../../pages/mp/base/MpItemStatProcess';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { useMessageModalStore } from 'stores/useMessageModalStore';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { CommonCode } from 'models/admin/CommonCode';
import useSessionStore from 'stores/useSessionStore';

type Props = {
  open: boolean;
  close: () => void;
  mpItemId: string;
  mcrcIds: string;
};

const MpItemStatHisModal = ({ open, close, mpItemId, mcrcIds }: Props) => {
  const { t } = useTranslation();
  const statRef = useRef<any>(null);
  const gridRef = useRef<any>();
  // row data
  const [rowDataMpItemStatHis, setRowDataMpItemStatHis] = useState<MpItem[]>([]);

  // process steps code
  const [getProcessStepsCode, setProcessStepsCode] = useState<Code[]>([]);

  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const userSession = useSessionStore();
  const { setMessageModalStateWhenModalOpen } = useMessageModalStore();

  // 분과위 권한에 따라 변경사유 저장이 가능
  const mcrcAuth = useMemo(() => {
    const mcrcId = mcrcIds.split(',').map((name) => name.trim());
    return mcrcId.includes(userSession.userId);
  }, []);

  // init
  useEffect(() => {
    getCommonCodes();

    handleSearch();
  }, []);

  // commonn code set
  const getCommonCodes = async () => {
    const mpItemStatCd: Code[] = await getCommonCodeNames('MP_ITEM_STAT_MAP');
    setProcessStepsCode(mpItemStatCd);

    // setProcessStepsCode(mpItemStatCd.filter((o) => o.cmnCd !== 'DROP'));
  };

  const handleSearch = () => {
    if (isEmpty(mpItemId)) return;
    findMpItemStatHis(mpItemId).then((result) => {
      setRowDataMpItemStatHis(result);
    });
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
    console.log('onInitialized... ');
  };

  //MP유형(단계), 상태, 이벤트, 비고, 등록자, 등록일자 -> 진행단계, 이벤트, 상태, 변경사유, 등록자, 상태변경일자로 변경
  const layoutDefinition = [
    {
      binding: 'chgHisId',
      visible: false,
    },
    {
      header: String(t('com.label.상태', '상태')),
      binding: 'crudKey',
      width: 50,
      isReadOnly: true,
      align: 'center',
      cellTemplate: GridStatusCellTemplate,
    },
    {
      header: String(t('com.label.진행단계', '진행단계')),
      binding: 'itemTpCdNm',
      width: 120,
      isReadOnly: true,
    },
    {
      header: String(t('com.label.이벤트', '이벤트')),
      binding: 'occrEvtNm',
      width: 225,
      align: 'left',
      isReadOnly: true,
    },
    {
      header: String(t('com.label.상태', '상태')),
      binding: 'itemStatCdNm',
      align: 'left',
      width: 120,
      isReadOnly: true,
    },

    {
      header: String(t('com.label.변경사유', '변경사유')),
      binding: 'rmk',
      align: 'left',
      width: '*',
      isReadOnly: !mcrcAuth,
    },
    {
      header: String(t('com.label.등록자', '등록자')),
      binding: 'dataInsUserNm',
      width: 100,
      isReadOnly: true,
    },
    {
      header: String(t('com.label.상태변경일자', '상태변경일자')),
      binding: 'dataInsDtm',
      width: 100,
      isReadOnly: true,
    },
  ];

  // process steps bar set
  const processSteps = useMemo(() => {
    let currentStepNo = -1;

    if (rowDataMpItemStatHis.length === 0) return;

    const isDropChk = rowDataMpItemStatHis[0]?.itemStatCd === 'MP010'; // 현재 DROP
    const isDe = rowDataMpItemStatHis.findIndex((i) => i.itemStatCd === 'MP007'); //수평전개보류

    let filteredSteps = (getProcessStepsCode || []).filter(
      (o) =>
        o.cmnCd !== 'DROP' &&
        (rowDataMpItemStatHis[0].fstrYn === 'Y' ? o.optValCtn1 !== 'HE' : o.optValCtn1 !== 'MPHE')
    );
    if (isDe == -1) {
      filteredSteps = filteredSteps.filter((o) => o.optValCtn2 !== 'MP007');
    }

    if (isDropChk) {
      const dropAddIdx = getProcessStepsCode.findIndex(
        (o) =>
          o.optValCtn1 === rowDataMpItemStatHis[1].mpItemTpCd &&
          o.optValCtn2 === rowDataMpItemStatHis[1].itemStatCd
      );

      if (dropAddIdx !== -1) {
        filteredSteps = getProcessStepsCode.slice(0, dropAddIdx + 1);
      }
      filteredSteps = [
        ...filteredSteps,
        {
          cmnCd: 'DROP',
          cmnCdNm: 'DROP',
          cmnCdDesc: '',
          optValCtn1: rowDataMpItemStatHis[0]?.mpItemTpCd,
          optValCtn2: rowDataMpItemStatHis[0]?.itemStatCd,
        },
      ];
    }
    return (filteredSteps || []).reduce((acc, cur, idx) => {
      const isNow =
        rowDataMpItemStatHis[0].itemStatCd === cur.optValCtn2 &&
        rowDataMpItemStatHis[0].mpItemTpCd === cur.optValCtn1;

      if (isNow) currentStepNo = idx;
      return [
        ...acc,
        {
          ...cur,
          cmnCdDesc: isNow
            ? 'now'
            : Object.keys(rowDataMpItemStatHis || {}).length > 0 && currentStepNo < 0
            ? 'done'
            : '',
        },
      ];
    }, [] as Code[]);
  }, [getProcessStepsCode, rowDataMpItemStatHis]);

  const btnSave = async () => {
    const saveRows = rowDataMpItemStatHis
      .map((rowNode) => {
        return rowNode.crudKey ? rowNode : null;
      })
      .filter((element) => element !== null) as CommonCode[];

    if (saveRows.length == 0) {
      openMessageBar({
        type: 'error',
        content: t('com.label.수정된 항목이 없습니다.', '수정된 항목이 없습니다.'),
      });
      return true;
    }
    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        saveMpItemStatHis(saveRows).then((result) => {
          if (!result) {
            openMessageBar({
              type: 'error',
              content: t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
            return false;
          } else {
            openMessageBar({
              type: 'confirm',
              content: t('com.label.저장되었습니다.', '저장되었습니다.'),
            });
          }
          gridRef.current.refresh();
        });
      },
      noCallback: () => {
        return false;
      },
    });
  };

  const dialogButtons = [
    <Button key={'save'} css={IconButton.button} className="save" disableRipple onClick={btnSave}>
      {t('com.button.저장', '저장')}
    </Button>,
  ];

  return (
    <CustomDialog
      open={true}
      title={t('mp.label.MP품목 상태 변경 이력', 'MP품목 상태 변경 이력')}
      size={'xxl'}
      buttons={mcrcAuth ? dialogButtons : null}
      onCancel={mcrcAuth ? close : undefined}
      onClose={close}
    >
      <TableContainer css={tb.table}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell width={'120'}>{t('mp.label.Item 번호', 'Item 번호')}</TableCell>
              <TableCell width={'120'}>
                {rowDataMpItemStatHis.length > 0 && rowDataMpItemStatHis[0].itemId}
              </TableCell>
              <TableCell>{t('mp.label.Item 명', 'Item 명')}</TableCell>
              <TableCell>
                {rowDataMpItemStatHis.length > 0 && rowDataMpItemStatHis[0].itemEngNm}
              </TableCell>
              <TableCell width={'120'}>{t('mp.label.분류', '분류')}</TableCell>
              <TableCell width={'120'}>
                {rowDataMpItemStatHis.length > 0 && rowDataMpItemStatHis[0].mpClsfCdNm}
              </TableCell>
              <TableCell width={'120'}>{t('mp.label.상태', '상태')}</TableCell>
              <TableCell width={'120'}>
                {rowDataMpItemStatHis.length > 0 && rowDataMpItemStatHis[0].itemStatCdNm}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <MpItemStatProcess ref={statRef} mpItemId={rowDataMpItemStatHis[0]?.itemId} />
      {/* todo::: 진행 상태바 process step start  */}
      {/*<ProcessBarWrap className="mt24" style={{ height: '60px' }}>*/}
      {/*  {(processSteps || []).map((o, i) => (*/}
      {/*    <ProcessStepBox key={o.cmnCd} className={o.cmnCd == 'B' ? 'none' : o.cmnCdDesc}>*/}
      {/*      /!* done, now, disable o.cmnCd == 'B' ? 'done' : o.cmnCdDesc *!/*/}
      {/*      <StepBox>*/}
      {/*        <ProcessStep>*/}
      {/*          <ProcessNum>{i + 1}</ProcessNum>*/}
      {/*          <ProcessText>{o.cmnCdNm}</ProcessText>*/}
      {/*        </ProcessStep>*/}
      {/*      </StepBox>*/}
      {/*    </ProcessStepBox>*/}
      {/*  ))}*/}
      {/*  <ProgressBar></ProgressBar>*/}
      {/*</ProcessBarWrap>*/}
      {/*process step end*/}

      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowDataMpItemStatHis}
        height={300}
        align={'center'}
        allowPinning={false}
        isFilter={false}
        isSelector={false}
        initialized={onInitialized}
      />
    </CustomDialog>
  );
};

export default MpItemStatHisModal;
